import { GATES, pins } from "./registry";
export const bit = (v) =>
  v === 0 || v === "0" ? 0 : v === 1 || v === "1" ? 1 : "X";
export const inv = (v) => (bit(v) === "X" ? "X" : 1 - bit(v));
export const and = (...vs) =>
  vs.some((v) => bit(v) === 0) ? 0 : vs.every((v) => bit(v) === 1) ? 1 : "X";
export const or = (...vs) =>
  vs.some((v) => bit(v) === 1) ? 1 : vs.every((v) => bit(v) === 0) ? 0 : "X";
export const xor = (...vs) =>
  vs.some((v) => bit(v) === "X") ? "X" : vs.reduce((a, v) => a ^ bit(v), 0);
export const merge = (values) =>
  values.every((v) => v === values[0]) ? values[0] : "X";
const possibilities = (v) => (bit(v) === "X" ? [0, 1] : [bit(v)]);
export function fullAdd(a, b, c) {
  return { sum: xor(a, b, c), carry: or(and(a, b), and(a, c), and(b, c)) };
}
export function evaluate(c, values) {
  if (c.type === "CONST0" || c.type === "CONST1")
    return { Y: c.type === "CONST1" ? 1 : 0 };
  const p = pins(c),
    v = (n) => bit(values[n]),
    cfg = c.config;
  if (GATES.includes(c.type)) {
    const a = p.inputs.map((i) => v(i.id));
    const base = c.type
      .replace("NAND", "AND")
      .replace("NOR", "OR")
      .replace("XNOR", "XOR");
    const r =
      base === "NOT"
        ? inv(a[0])
        : base === "AND"
          ? and(...a)
          : base === "OR"
            ? or(...a)
            : xor(...a);
    return { Y: ["NAND", "NOR", "XNOR"].includes(c.type) ? inv(r) : r };
  }
  const outputs = {};
  const possibleIndex = (prefix, count) =>
    Array.from({ length: 2 ** count }, (_, i) => i).filter((i) =>
      Array.from({ length: count }, (_, j) => j).every(
        (j) => v(prefix + j) === "X" || v(prefix + j) === ((i >> j) & 1),
      ),
    );
  switch (c.type) {
    case "MUX": {
      const choices = possibleIndex("S", Math.log2(cfg.size));
      return { Y: merge(choices.map((i) => v("D" + i))) };
    }
    case "DEMUX": {
      const choices = possibleIndex("S", Math.log2(cfg.size));
      for (let i = 0; i < cfg.size; i++)
        outputs["Y" + i] = choices.includes(i)
          ? choices.length === 1
            ? v("D")
            : merge([0, v("D")])
          : 0;
      return outputs;
    }
    case "DECODER": {
      const choices = possibleIndex("A", cfg.size);
      for (let i = 0; i < 2 ** cfg.size; i++)
        outputs["Y" + i] = and(
          cfg.enabled ? v("EN") : 1,
          choices.includes(i) ? (choices.length === 1 ? 1 : "X") : 0,
        );
      return outputs;
    }
    case "ENCODER":
    case "PRIORITY": {
      const count = cfg.size,
        unknown = [];
      let mask = 0;
      for (let i = 0; i < count; i++) {
        if (v("D" + i) === 1) mask |= 1 << i;
        else if (v("D" + i) === "X") unknown.push(i);
      }
      const rows = [];
      for (let a = 0; a < 2 ** unknown.length; a++) {
        let m = mask;
        unknown.forEach((j, k) => {
          if ((a >> k) & 1) m |= 1 << j;
        });
        const active = Array.from({ length: count }, (_, i) => i).filter(
          (i) => (m >> i) & 1,
        );
        const valid =
          c.type === "PRIORITY" ? active.length > 0 : active.length === 1;
        const code = active[active.length - 1] || 0;
        rows.push({
          V: valid ? 1 : 0,
          ...Object.fromEntries(
            Array.from({ length: Math.ceil(Math.log2(count)) }, (_, i) => [
              "Y" + i,
              valid ? (code >> i) & 1 : c.type === "PRIORITY" ? 0 : "X",
            ]),
          ),
        });
      }
      p.outputs.forEach(
        (o) => (outputs[o.id] = merge(rows.map((row) => row[o.id]))),
      );
      return outputs;
    }
    case "HA":
      return { Sum: xor(v("A"), v("B")), Carry: and(v("A"), v("B")) };
    case "FA": {
      const r = fullAdd(v("A"), v("B"), v("Cin"));
      return { Sum: r.sum, Cout: r.carry };
    }
    case "PA": {
      let carry = v("Cin");
      for (let i = 0; i < cfg.width; i++) {
        const r = fullAdd(v("A" + i), v("B" + i), carry);
        outputs["Sum" + i] = r.sum;
        carry = r.carry;
      }
      return { ...outputs, Cout: carry };
    }
    case "HS":
      return { Diff: xor(v("A"), v("B")), Borrow: and(inv(v("A")), v("B")) };
    case "FS":
      return {
        Diff: xor(v("A"), v("B"), v("Bin")),
        Bout: or(
          and(inv(v("A")), v("B")),
          and(inv(xor(v("A"), v("B"))), v("Bin")),
        ),
      };
    case "COMPARE": {
      let outcomes = new Set(["EQ"]);
      for (let i = cfg.width - 1; i >= 0; i--) {
        const next = new Set();
        for (const prev of outcomes)
          for (const a of possibilities(v("A" + i)))
            for (const b of possibilities(v("B" + i)))
              next.add(
                prev === "EQ" ? (a > b ? "GT" : a < b ? "LT" : "EQ") : prev,
              );
        outcomes = next;
      }
      for (const k of ["GT", "EQ", "LT"])
        outputs[k] =
          outcomes.size === 1
            ? outcomes.has(k)
              ? 1
              : 0
            : outcomes.has(k)
              ? "X"
              : 0;
      return outputs;
    }
    default:
      return Object.fromEntries(p.outputs.map((p) => [p.id, "X"]));
  }
}
export function nextState(c, inputs, previous) {
  const v = (k) => bit(inputs[k]),
    q = bit(previous),
    type = c.type;
  if (type === "D_FF")
    return { value: v("D"), rule: "Capture D", forbidden: false };
  if (type === "T_FF")
    return {
      value: xor(q, v("T")),
      rule:
        v("T") === 0
          ? "Hold"
          : v("T") === 1
            ? "Toggle"
            : "Unknown toggle input",
      forbidden: false,
    };
  const a = type === "JK_FF" ? v("J") : v("S"),
    b = type === "JK_FF" ? v("K") : v("R");
  const results = [];
  for (const aa of possibilities(a))
    for (const bb of possibilities(b)) {
      if (aa === 0 && bb === 0) results.push(q);
      else if (aa === 0) results.push(0);
      else if (bb === 0) results.push(1);
      else if (type === "JK_FF") results.push(inv(q));
      else results.push(c.config.policy === "HOLD" ? q : bit(c.config.policy));
    }
  const forbidden = type !== "JK_FF" && a === 1 && b === 1;
  const rule = forbidden
    ? `S=R=1: classroom policy ${c.config.policy}`
    : a === "X" || b === "X"
      ? "Merge all possible characteristic-table results"
      : !a && !b
        ? "Hold"
        : !a
          ? "Reset"
          : !b
            ? "Set"
            : "Toggle";
  return { value: merge(results), rule, forbidden };
}
